import React, { useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { getDashboard } from '../../redux/actions/dashboardAction'
import { FILE_TYPE_ICON, formatBytes, formatDate, formatMoney, getErrorMessage } from '../../utils'

const TYPE_LABELS = { image: 'Images', pdf: 'PDF Files', word: 'Word Docs', excel: 'Excel / CSV', text: 'Text Files', other: 'Other Files' }

const Dashboard = () => {
    const dispatch = useDispatch()
    const { stats, loader } = useSelector(state => state.dashboard)

    const load = useCallback(() => {
        dispatch(getDashboard()).catch(err => toast.error(getErrorMessage(err)))
    }, [dispatch])

    useEffect(() => { load() }, [load])

    const cards = [
        { icon: 'fa-cubes', value: stats.totalItems ?? 0, label: 'Total Items' },
        { icon: 'fa-check-circle', value: stats.activeItems ?? 0, label: 'Active Items' },
        { icon: 'fa-archive', value: stats.totalQuantity ?? 0, label: 'Total Stock Qty' },
        { icon: 'fa-inr', value: formatMoney(stats.stockValue), label: 'Stock Value' },
        { icon: 'fa-files-o', value: stats.totalDocuments ?? 0, label: 'Documents Uploaded' },
        { icon: 'fa-database', value: formatBytes(stats.storageUsed), label: 'Storage Used' },
        { icon: 'fa-magic', value: stats.scannedDocuments ?? 0, label: 'Documents Scanned' },
        { icon: 'fa-download', value: stats.importedItems ?? 0, label: 'Items from Scans' },
    ]
    const byType = stats.documentsByType || {}

    return (
        <>
            <div className="Small-Wrapper">
                <div className="TitleBox">
                    <h2>Dashboard</h2>
                    <button className="Button" onClick={load} disabled={loader}>
                        <i className={`fa fa-refresh ${loader ? 'fa-spin' : ''}`}></i> Refresh
                    </button>
                </div>
                <br />
                <div className="DashboardBox">
                    <ul>
                        {cards.map(card => (
                            <li key={card.label}>
                                <aside>
                                    <span className="Icon"><i className={`fa ${card.icon}`} aria-hidden="true"></i></span>
                                    <h3>{card.value}</h3>
                                    <p>{card.label}</p>
                                </aside>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="Small-Wrapper">
                <div className="TitleBox">
                    <h2>Documents by Type</h2>
                    <Link className="TitleLink" to="/doc-upload"><i className="fa fa-upload"></i> Upload Documents</Link>
                </div>
                <div className="row">
                    {Object.keys(TYPE_LABELS).map(type => (
                        <div className="col-sm-6 col-lg-4" key={type}>
                            <div className={`Overview Overview-${type}`}>
                                <h2>{TYPE_LABELS[type]}</h2>
                                <aside>
                                    <i className={`fa ${FILE_TYPE_ICON[type]}`}></i>
                                    <h3>{byType[type] || 0}</h3>
                                </aside>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="row DashboardLists">
                <div className="col-lg-6">
                    <div className="Small-Wrapper">
                        <div className="TitleBox">
                            <h2>Recent Items</h2>
                            <Link className="TitleLink" to="/item-manager">View All</Link>
                        </div>
                        <div className="TableList">
                            <table>
                                <thead>
                                    <tr><th>Name</th><th>Category</th><th>Qty</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {(stats.recentItems || []).map(item => (
                                        <tr key={item._id}>
                                            <td>{item.name}</td>
                                            <td>{item.category}</td>
                                            <td>{item.quantity}</td>
                                            <td><span className={item.status === 'Active' ? 'Green' : 'Red'}>{item.status}</span></td>
                                        </tr>
                                    ))}
                                    {!stats.recentItems?.length && <tr><td colSpan="4">No items yet</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="Small-Wrapper">
                        <div className="TitleBox">
                            <h2>Recent Uploads</h2>
                            <Link className="TitleLink" to="/doc-upload">View All</Link>
                        </div>
                        <div className="TableList">
                            <table>
                                <thead>
                                    <tr><th>File</th><th>Size</th><th>Uploaded</th></tr>
                                </thead>
                                <tbody>
                                    {(stats.recentDocuments || []).map(doc => (
                                        <tr key={doc._id}>
                                            <td className="text-left">
                                                <i className={`fa ${FILE_TYPE_ICON[doc.fileType]} FileIcon FileIcon-${doc.fileType}`}></i> {doc.title}
                                            </td>
                                            <td>{formatBytes(doc.size)}</td>
                                            <td>{formatDate(doc.createdAt)}</td>
                                        </tr>
                                    ))}
                                    {!stats.recentDocuments?.length && <tr><td colSpan="3">No documents yet</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Dashboard
